import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Edit,
  Trash2,
  Building,
  Users,
  UserCheck,
  Search,
  Save,
  X,
  AlertCircle,
  Eye,
  Download
} from 'lucide-react';
import departmentRoleService from '../../../api/services/departmentRoleService';
import locationService from '../../../api/services/locationService';
import toast from 'react-hot-toast';
import { formatDate, capitalizeWords } from '../../../utils/stringUtils';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import ExportModal from '../../../components/common/ExportModal';
import Button from '../../../components/common/Button/Button';
import { API_BASE_URL, API_ENDPOINTS } from '../../../api/config';
import { ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from '../../../utils/formValidation';

const MAX_LOCATION_LABEL_LENGTH = 45;

const buildLocationLabel = (location) => {
  if (!location) return 'Unknown Location';

  const baseName = location.name || location.label || location.value || location.fullAddress || 'Unknown Location';

  // Handle potentially nested objects from new structure (e.g. { name: 'City' })
  let city = location.address?.city || location.city;
  let state = location.address?.state || location.state;

  if (typeof city === 'object' && city !== null) city = city.name;
  if (typeof state === 'object' && state !== null) state = state.name;

  const labelParts = [capitalizeWords(String(baseName).trim())];

  if (city && typeof city === 'string') {
    labelParts.push(`- ${capitalizeWords(city.trim())}`);
  }
  if (state && typeof state === 'string') {
    labelParts.push(`, ${capitalizeWords(state.trim())}`);
  }

  const fullLabel = labelParts.join(' ').replace(/\s+/g, ' ').trim();
  if (fullLabel.length <= MAX_LOCATION_LABEL_LENGTH) {
    return fullLabel;
  }

  return `${fullLabel.slice(0, MAX_LOCATION_LABEL_LENGTH - 1)}…`;
};

const DepartmentRoleMapping = () => {
  const showSuccess = (message) => toast.success(message);
  const showError = (message) => toast.error(message);
  const queryClient = useQueryClient();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [activeTab, setActiveTab] = useState('departments');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [modalType, setModalType] = useState('add'); // add, edit
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  // Selection state
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const bulkDeletingRef = React.useRef(false); // Guard against double-invocation
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(new Set(departments.map(item => item.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return newSelected;
    });
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="h-3 w-3 ml-1 text-gray-400 group-hover:text-gray-600 transition-colors" />;
    }
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="h-3 w-3 ml-1 text-[#0F2942]" /> :
      <ChevronDown className="h-3 w-3 ml-1 text-[#0F2942]" />;
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      handleExportSelected();
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    setExportLoading(true);
    try {
      const exportData = departments.map(dept => ({
        'Department Name': dept.name,
        'Code': dept.code,
        'Description': dept.description || 'N/A',
        'Location': typeof dept.location === 'object' ? buildLocationLabel(dept.location) : (dept.location || 'N/A'),
        'Head': dept.head ? `${dept.head.personalInfo?.firstName || ''} ${dept.head.personalInfo?.lastName || ''}`.trim() : 'N/A',
        'Parent': dept.parentDepartment ? dept.parentDepartment.name : 'N/A',
        'Status': (dept.status || (dept.isActive ? 'Active' : 'Inactive')),
        'Created At': dept.createdAt ? formatDate(dept.createdAt) : 'N/A'
      }));

      exportToCSV(exportData, 'departments-current-page', {
        title: 'Department List',
        type: 'Current Page'
      });
      showSuccess('Current page exported successfully');
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      showError('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportSelected = () => {
    if (selectedRows.size === 0) {
      showError('No rows selected for export');
      return;
    }

    setExportLoading(true);
    try {
      const selectedData = departments.filter(item => selectedRows.has(item.id));
      const exportData = selectedData.map(dept => ({
        'Department Name': dept.name,
        'Code': dept.code,
        'Description': dept.description || 'N/A',
        'Location': typeof dept.location === 'object' ? buildLocationLabel(dept.location) : (dept.location || 'N/A'),
        'Head': dept.head ? `${dept.head.personalInfo?.firstName || ''} ${dept.head.personalInfo?.lastName || ''}`.trim() : 'N/A',
        'Parent': dept.parentDepartment ? dept.parentDepartment.name : 'N/A',
        'Status': (dept.status || (dept.isActive ? 'Active' : 'Inactive')),
        'Created At': dept.createdAt ? formatDate(dept.createdAt) : 'N/A'
      }));

      exportToCSV(exportData, 'departments-selected', {
        title: 'Department List',
        type: 'Selected Data'
      });
      showSuccess(`Successfully exported ${selectedRows.size} departments`);
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      showError('Failed to export selected data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('type', 'All Data');
      queryParams.append('isActive', statusFilter === 'all' ? 'all' : statusFilter === 'active' ? 'true' : 'false');
      if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);

      const url = `${API_ENDPOINTS.HR.DEPARTMENTS}/export?${queryParams.toString()}`;
      const filename = generateExportFilename('departments-all');
      await downloadFromBackend(url, filename);

      showSuccess('All departments data export started');
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      showError('Failed to export all departments');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('type', `Date Range (${startDate} to ${endDate})`);
      queryParams.append('startDate', startDate);
      queryParams.append('endDate', endDate);
      queryParams.append('isActive', statusFilter === 'all' ? 'all' : statusFilter === 'active' ? 'true' : 'false');
      if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);

      const url = `${API_ENDPOINTS.HR.DEPARTMENTS}/export?${queryParams.toString()}`;
      const filename = generateExportFilename('departments-range');
      await downloadFromBackend(url, filename);

      showSuccess('Department range export started');
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      showError('Failed to export department range');
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, dateRange.startDate, dateRange.endDate]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    // manager: '',
    // parentDepartment: '',
    status: 'Active',
    code: '',
    location: '',
    // costCenter: '',
    level: 'Individual Contributor'
  });

  // Fetch active locations using React Query
  const { data: locationsData, isLoading: loadingLocations } = useQuery({
    queryKey: ['activeLocations', 'raw'],
    queryFn: async () => {
      try {
        const result = await locationService.getLocations({ isActive: 'true', limit: 1000 });
        console.log('📍 Active Locations API result:', result);

        // Handle different response structures
        if (result.success && result.data) {
          // Case 1: result.data.data.locations (nested structure from API)
          if (result.data.data?.locations && Array.isArray(result.data.data.locations)) {
            console.log('✅ Found locations in result.data.data.locations:', result.data.data.locations.length);
            return result.data.data.locations;
          }
          // Case 2: result.data.locations (direct structure)
          if (result.data.locations && Array.isArray(result.data.locations)) {
            console.log('✅ Found locations in result.data.locations:', result.data.locations.length);
            return result.data.locations;
          }
          // Case 3: result.data is directly an array
          if (Array.isArray(result.data)) {
            console.log('✅ Found locations as direct array:', result.data.length);
            return result.data;
          }
        }
        console.warn('⚠️ No locations found in expected structure');
        return [];
      } catch (error) {
        console.error('❌ Error fetching active locations:', error);
        return [];
      }
    },
    staleTime: 0,
    retry: 2, // Retry failed requests twice
  });

  const locations = locationsData || [];

  // Update location value when locations are loaded during edit
  useEffect(() => {
    const locationsList = locationsData || [];
    if (showModal && modalType === 'edit' && selectedItem && locationsList.length > 0) {
      const itemLocation = selectedItem.location;

      // If location is already an object with ID, use it directly
      if (itemLocation && typeof itemLocation === 'object') {
        const correctValue = itemLocation._id || itemLocation.id;
        if (correctValue) {
          setFormData(prev => {
            if (prev.location !== correctValue) {
              return { ...prev, location: correctValue };
            }
            return prev;
          });
          return;
        }
      }

      // If location is a string (legacy data) or object without ID, try to match by name/address
      let searchValue = '';
      if (typeof itemLocation === 'string') {
        searchValue = itemLocation;
      } else if (itemLocation && typeof itemLocation === 'object') {
        searchValue = itemLocation.name || itemLocation.fullAddress || itemLocation.label || '';
      }

      if (searchValue) {
        searchValue = searchValue.trim();

        // Find matching location in the list
        const matchingLocation = locationsList.find(loc => {
          const locId = loc._id || loc.id;
          const locName = (loc.name || '').trim();
          const locFullAddress = (loc.fullAddress || loc.address?.fullAddress || '').trim();
          const locLabel = buildLocationLabel(loc).trim();

          // strict match on ID first (if searchValue happens to be an ID)
          if (locId === searchValue) return true;

          // standard string matching
          if (locName.toLowerCase() === searchValue.toLowerCase() ||
            locFullAddress.toLowerCase() === searchValue.toLowerCase() ||
            locLabel.toLowerCase() === searchValue.toLowerCase()) {
            return true;
          }

          return false;
        });

        if (matchingLocation) {
          const correctValue = matchingLocation._id || matchingLocation.id;
          setFormData(prev => {
            if (prev.location !== correctValue) {
              return { ...prev, location: correctValue };
            }
            return prev;
          });
        }
      }
    }
  }, [locationsData, modalType, selectedItem, showModal]);

  // Character limits for fields
  const FIELD_LIMITS = {
    name: 100,
    description: 500,
    code: 50,
  };

  // Apply standardized security sanitization (allows all approved symbols)
  const filterAllowedDepartmentInput = (value, maxLength = Infinity) => {
    const filtered = value.replace(INVALID_CHARS_REGEX, '');
    return maxLength !== Infinity ? filtered.slice(0, maxLength) : filtered;
  };

  // Fetch departments using React Query
  const normalizeDepartmentsResponse = (result) => {
    const basePagination = {
      currentPage,
      totalPages: 1,
      totalCount: 0,
      hasNextPage: false,
      hasPrevPage: false
    };

    if (!result) {
      return { departments: [], pagination: basePagination };
    }

    const payloadCandidates = [
      result,
      result.data,
      result.data?.data
    ];

    for (const payload of payloadCandidates) {
      if (payload && Array.isArray(payload.departments)) {
        const pagination = payload.pagination || basePagination;
        return {
          departments: payload.departments,
          pagination: {
            currentPage: pagination.currentPage || currentPage,
            totalPages: Math.max(pagination.totalPages || 1, 1),
            totalCount: pagination.totalCount ?? payload.departments.length,
            hasNextPage: !!pagination.hasNextPage,
            hasPrevPage: !!pagination.hasPrevPage
          }
        };
      }

      if (payload && Array.isArray(payload)) {
        return {
          departments: payload,
          pagination: {
            ...basePagination,
            totalCount: payload.length
          }
        };
      }
    }

    console.warn('⚠️ Departments response in unexpected format:', result);
    return { departments: [], pagination: basePagination };
  };

  const {
    data: departmentsData,
    isLoading: loadingDepartments,
    error: departmentsError,
    refetch: refetchDepartments
  } = useQuery({
    queryKey: ['departments', statusFilter, debouncedSearchTerm, currentPage, pageSize, dateRange.startDate, dateRange.endDate],
    queryFn: async () => {
      try {
        const params = {
          page: currentPage,
          limit: pageSize,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          isActive: statusFilter === 'all' ? 'all' : statusFilter === 'active' ? 'true' : 'false',
          paginated: 'true',
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        };

        if (debouncedSearchTerm.trim()) {
          params.search = debouncedSearchTerm.trim();
        }

        const result = await departmentRoleService.getDepartments(params);
        console.log('📊 Departments API raw result:', result);
        return normalizeDepartmentsResponse(result);
      } catch (error) {
        console.error('❌ Error fetching departments:', error);
        throw error;
      }
    },
    staleTime: 0,
    retry: 2,
  });

  const departments = departmentsData?.departments || [];
  const pagination = departmentsData?.pagination || {
    currentPage,
    totalPages: 1,
    totalCount: departments.length,
    hasNextPage: false,
    hasPrevPage: false
  };

  // Combined loading state
  const loading = loadingDepartments; // || loadingRoles || loadingManagers;
  const error = departmentsError; // || rolesError || managersError;

  const displayedDepartments = useMemo(() => {
    if (!departments.length) return [];

    const sorted = [...departments];
    const { key, direction } = sortConfig;

    return sorted.sort((a, b) => {
      let aVal, bVal;

      switch (key) {
        case 'code':
          aVal = (a.code || '').toLowerCase();
          bVal = (b.code || '').toLowerCase();
          break;
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'location':
          aVal = buildLocationLabel(a.location).toLowerCase();
          bVal = buildLocationLabel(b.location).toLowerCase();
          break;
        case 'isActive':
          aVal = (a.status || (a.isActive ? 'Active' : 'Inactive')).toLowerCase();
          bVal = (b.status || (b.isActive ? 'Active' : 'Inactive')).toLowerCase();
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt || 0).getTime();
          bVal = new Date(b.createdAt || 0).getTime();
          break;
        case 'updatedAt':
          aVal = new Date(a.updatedAt || 0).getTime();
          bVal = new Date(b.updatedAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [departments, sortConfig]);
  const startItem = pagination.totalCount === 0 ? 0 : ((pagination.currentPage - 1) * pageSize) + 1;
  const endItem = pagination.totalCount === 0 ? 0 : startItem + displayedDepartments.length - 1;
  const totalPages = Math.max(pagination.totalPages, 1);
  const totalDepartments = pagination.totalCount ?? departments.length;
  const showPaginationControls = pagination.totalCount > pageSize;
  const canGoPrev = pagination.hasPrevPage && pagination.currentPage > 1;
  const canGoNext = pagination.hasNextPage && pagination.currentPage < totalPages;

  const handlePageSizeChange = (event) => {
    const nextSize = Number(event.target.value);
    setPageSize(nextSize);
    setCurrentPage(1);
  };

  // State for form errors
  const [errors, setErrors] = useState({});

  // Handle key press for text inputs (field-specific restrictions using centralized standard)
  const handleTextKeyPress = (e) => {
    if (['name', 'description', 'code'].includes(e.target.name)) {
      if (INVALID_CHARS_REGEX.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
      }
      return;
    }

    if (INVALID_CHARS_REGEX.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    // Apply field-specific input handling
    if (name === 'name') {
      processedValue = filterAllowedDepartmentInput(value, FIELD_LIMITS.name);
    } else if (name === 'description') {
      processedValue = filterAllowedDepartmentInput(value, FIELD_LIMITS.description);
    } else if (name === 'code') {
      processedValue = filterAllowedDepartmentInput(value, FIELD_LIMITS.code);
    }

    const updatedFormData = {
      ...formData,
      [name]: processedValue
    };

    setFormData(updatedFormData);

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Check for duplicates in real-time when name, code, or location changes
    if ((name === 'name' || name === 'code' || name === 'location') && activeTab === 'departments') {
      const locationId = name === 'location' ? processedValue : updatedFormData.location?.trim();
      const deptName = name === 'name' ? processedValue : updatedFormData.name?.trim();
      const deptCode = name === 'code' ? processedValue : updatedFormData.code?.trim();

      if (locationId && deptName && deptCode) {
        // Helper to check if locations match (handles ID vs Object vs String)
        const isSameLocation = (deptLoc) => {
          if (!deptLoc) return false;

          // Case 1: deptLoc is object, locationId is ID
          if (typeof deptLoc === 'object') {
            const dId = deptLoc._id || deptLoc.id;
            return dId === locationId;
          }

          // Case 2: deptLoc is string (legacy), locationId is ID
          // Try to match string name with selected location's name
          const selectedLoc = locations.find(l => (l._id || l.id) === locationId);
          if (selectedLoc) {
            const locName = selectedLoc.name || '';
            const locAddress = selectedLoc.fullAddress || '';
            // Simple string match
            return (typeof deptLoc === 'string') && (
              deptLoc.toLowerCase() === locName.toLowerCase() ||
              deptLoc.toLowerCase() === locAddress.toLowerCase()
            );
          }

          return false;
        };

        // Check for duplicate name
        if (name === 'name' && deptName) {
          const duplicateName = departments.find(
            dept => isSameLocation(dept.location) &&
              dept.name.toLowerCase() === deptName.toLowerCase() &&
              (modalType === 'add' || dept.id !== selectedItem?.id)
          );
          if (duplicateName) {
            setErrors(prev => ({
              ...prev,
              name: `Department with name "${deptName}" already exists in this location`
            }));
          }
        }

        // Check for duplicate code
        if (name === 'code' && deptCode) {
          const duplicateCode = departments.find(
            dept => isSameLocation(dept.location) &&
              dept.code.toLowerCase() === deptCode.toLowerCase() &&
              (modalType === 'add' || dept.id !== selectedItem?.id)
          );
          if (duplicateCode) {
            setErrors(prev => ({
              ...prev,
              code: `Department with code "${deptCode}" already exists in this location`
            }));
          }
        }

        // If location changed, check both name and code
        if (name === 'location' && locationId) {
          const duplicateName = deptName ? departments.find(
            dept => isSameLocation(dept.location) &&
              dept.name.toLowerCase() === deptName.toLowerCase() &&
              (modalType === 'add' || dept.id !== selectedItem?.id)
          ) : null;

          const duplicateCode = deptCode ? departments.find(
            dept => isSameLocation(dept.location) &&
              dept.code.toLowerCase() === deptCode.toLowerCase() &&
              (modalType === 'add' || dept.id !== selectedItem?.id)
          ) : null;

          if (duplicateName) {
            setErrors(prev => ({
              ...prev,
              name: `Department with name "${deptName}" already exists in this location`
            }));
          }
          if (duplicateCode) {
            setErrors(prev => ({
              ...prev,
              code: `Department with code "${deptCode}" already exists in this location`
            }));
          }
        }
      }
    }
  };

  // Validate form
  // Validate values (helper for both form submit and field blur)
  const validateValues = (data) => {
    const validationErrors = {};

    // Name validation - required and standard security validation
    if (!data.name.trim()) {
      validationErrors.name = 'Name is required';
    } else if (data.name.trim().length < 2) {
      validationErrors.name = 'Name must be at least 2 characters long';
    } else if (!VALID_TEXT_REGEX.test(data.name.trim())) {
      validationErrors.name = 'Name contains invalid characters. Only standard symbols are allowed.';
    }

    // Description validation - required and standard security validation
    if (!data.description.trim()) {
      validationErrors.description = 'Description is required';
    } else if (data.description.trim() && !VALID_TEXT_REGEX.test(data.description.trim())) {
      validationErrors.description = 'Description contains invalid characters. Only standard symbols are allowed.';
    }

    // Department-specific validations
    if (activeTab === 'departments') {
      // Code validation - required and standard security validation
      if (!data.code || !data.code.trim()) {
        validationErrors.code = 'Department Code is required';
      } else if (data.code.trim().length < 2) {
        validationErrors.code = 'Department code must be at least 2 characters long';
      } else if (!VALID_TEXT_REGEX.test(data.code.trim())) {
        validationErrors.code = 'Department code contains invalid characters. Only standard symbols are allowed.';
      }

      // Location validation - required
      if (!data.location || !data.location.trim()) {
        validationErrors.location = 'Location is required';
      }
    }

    return validationErrors;
  };

  // Validate form
  const validateForm = () => {
    const validationErrors = validateValues(formData);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  // Field-level validation handler
  const handleFieldBlur = (e) => {
    const { name } = e.target;
    const currentErrors = validateValues(formData);

    if (currentErrors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: currentErrors[name]
      }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const getFieldError = (fieldName) => {
    return errors[fieldName] || '';
  };

  const getFieldClassName = (fieldName) => {
    const hasError = errors[fieldName];
    return `input-field ${hasError ? 'border-red-300 bg-red-50' : ''}`;
  };

  // Validate duplicate name or code in same location
  const validateDuplicate = () => {
    if (activeTab !== 'departments') return true;

    const locationId = formData.location?.trim();
    const name = formData.name?.trim();
    const code = formData.code?.trim();

    if (!locationId || !name || !code) return true;

    // Helper to check if locations match (handles ID vs Object vs String)
    const isSameLocation = (deptLoc) => {
      if (!deptLoc) return false;

      // Case 1: deptLoc is object, locationId is ID
      if (typeof deptLoc === 'object') {
        const dId = deptLoc._id || deptLoc.id;
        return dId === locationId;
      }

      // Case 2: deptLoc is string (legacy), locationId is ID
      const selectedLoc = locations.find(l => (l._id || l.id) === locationId);
      if (selectedLoc) {
        const locName = selectedLoc.name || '';
        const locAddress = selectedLoc.fullAddress || '';
        return (typeof deptLoc === 'string') && (
          deptLoc.toLowerCase() === locName.toLowerCase() ||
          deptLoc.toLowerCase() === locAddress.toLowerCase()
        );
      }

      return false;
    };

    // Check if there's a department with same name or code in the same location
    const duplicateName = departments.find(
      dept => isSameLocation(dept.location) &&
        dept.name.toLowerCase() === name.toLowerCase() &&
        (modalType === 'add' || dept.id !== selectedItem?.id)
    );

    const duplicateCode = departments.find(
      dept => isSameLocation(dept.location) &&
        dept.code.toLowerCase() === code.toLowerCase() &&
        (modalType === 'add' || dept.id !== selectedItem?.id)
    );

    if (duplicateName) {
      setErrors(prev => ({
        ...prev,
        name: `Department with name "${name}" already exists in this location`
      }));
      return false;
    }

    if (duplicateCode) {
      setErrors(prev => ({
        ...prev,
        code: `Department with code "${code}" already exists in this location`
      }));
      return false;
    }

    return true;
  };

  // Handle add new item
  const handleAdd = () => {
    setModalType('add');
    setSelectedItem(null);
    setFormData({
      name: '',
      description: '',
      // manager: '',
      // parentDepartment: '',
      status: 'Active',
      code: '',
      location: '',
      // costCenter: '',
      level: 'Individual Contributor'
    });
    setShowModal(true);
  };

  // Handle view item
  const handleView = (item) => {
    setViewItem(item);
    setShowViewModal(true);
  };

  // Handle edit item
  const handleEdit = (item) => {
    setModalType('edit');
    setSelectedItem(item);

    // Set location value - will be matched and updated by useEffect when locations are loaded
    setFormData({
      name: item.name || '',
      description: item.description || '',
      // manager: managerName,
      // parentDepartment: parentDeptName,
      status: item.status || (item.isActive !== undefined ? (item.isActive ? 'Active' : 'Inactive') : 'Active'),
      code: item.code || '',
      location: (item.location && typeof item.location === 'object') ? (item.location._id || item.location.id) : (item.location || ''),
      // costCenter: item.costCenter || '',
      level: item.level || 'Individual Contributor'
    });
    setErrors({});
    setShowModal(true);
  };

  // Handle delete item
  const handleDeleteClick = (item) => {
    setSelectedItemToDelete(item);
    setShowDeleteModal(true);
  };

  // Handle actual delete
  const handleDelete = async () => {
    if (!selectedItemToDelete) return;

    if (activeTab === 'departments') {
      deleteDepartmentMutation.mutate(selectedItemToDelete.id);
    }
  };

  // Handle actual bulk delete
  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    if (bulkDeletingRef.current) return; // Prevent double-invocation
    if (activeTab !== 'departments') return;

    // Snapshot IDs BEFORE clearing state
    const idsToDelete = Array.from(selectedRows);

    // Immediately close the modal and clear selection BEFORE calling mutate.
    // This ensures any re-invocation finds selectedRows empty and exits early.
    bulkDeletingRef.current = true;
    setShowBulkDeleteModal(false);
    setSelectedRows(new Set());

    bulkDeleteDepartmentsMutation.mutate(idsToDelete);
  };

  // Create department mutation
  const createDepartmentMutation = useMutation({
    mutationFn: async (departmentData) => {
      try {
        const result = await departmentRoleService.createDepartment(departmentData);
        if (result && (result.responseCode === 200 || result.responseCode === 201 || result.id)) {
          return result;
        } else {
          throw new Error(result?.responseMessage || 'Failed to create department');
        }
      } catch (error) {
        // Extract error message from API response
        const errorMessage = error.response?.data?.responseMessage ||
          error.response?.data?.message ||
          error.message ||
          'Failed to add department. Please try again.';
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      showSuccess('Department added successfully!');
      setShowModal(false);
    },
    onError: (error) => {
      console.error('❌ Create department error:', error);
      showError(error.message || 'Failed to add department. Please try again.');
    },
  });

  // Update department mutation
  const updateDepartmentMutation = useMutation({
    mutationFn: async ({ id, departmentData }) => {
      try {
        const result = await departmentRoleService.updateDepartment(id, departmentData);
        if (result && (result.responseCode === 200 || result.responseCode === 201 || result.id)) {
          return result;
        } else {
          throw new Error(result?.responseMessage || 'Failed to update department');
        }
      } catch (error) {
        // Extract error message from API response
        const errorMessage = error.response?.data?.responseMessage ||
          error.response?.data?.message ||
          error.message ||
          'Failed to update department. Please try again.';
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      showSuccess('Department updated successfully!');
      setShowModal(false);
    },
    onError: (error) => {
      console.error('❌ Update department error:', error);
      showError(error.message || 'Failed to update department. Please try again.');
    },
  });

  // Delete department mutation
  const deleteDepartmentMutation = useMutation({
    mutationFn: async (departmentId) => {
      const result = await departmentRoleService.deleteDepartment(departmentId);
      if (result && (result.responseCode === 200 || result.responseCode === 201 || result.success)) {
        return result;
      } else {
        throw new Error(result?.responseMessage || 'Failed to delete department');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      showSuccess('Department deleted successfully');
      setShowDeleteModal(false);
      setSelectedItemToDelete(null);
    },
    onError: (error) => {
      console.error('❌ Delete department error:', error);
      showError(error.message || 'Failed to delete department. Please try again.');
    },
  });

  // Bulk delete departments mutation
  const bulkDeleteDepartmentsMutation = useMutation({
    mutationFn: async (departmentIds) => {
      console.log('🚀 bulkDeleteDepartmentsMutation.mutationFn called with:', departmentIds);
      // The service returns the already-unwrapped response body (apiClient interceptor handles it)
      const result = await departmentRoleService.bulkDeleteDepartments(departmentIds);
      if (result && (result.responseCode === 200 || result.responseCode === 201 || result.success)) {
        return result;
      }
      throw new Error(result?.responseMessage || 'Failed to delete departments');
    },
    onSuccess: (data) => {
      bulkDeletingRef.current = false;
      // Show success toast
      const count = data?.responseMessage || 'Departments deleted successfully';
      showSuccess(count);
      // Invalidate the departments query to fetch the fresh list
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
    onError: (error) => {
      bulkDeletingRef.current = false;
      // Re-open modal and restore selection is not possible here since state was cleared.
      // Just show the error — user can re-select and retry.
      console.error('❌ Bulk delete departments error:', error);
      showError(error.message || 'Failed to delete departments. Please try again.');
    },
  });

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form (this will set errors state)
    if (!validateForm()) {
      // Errors are already set in validateForm, just return
      return;
    }

    // Validate duplicate name or code in same location
    const isDuplicateValid = validateDuplicate();
    if (!isDuplicateValid) {
      // Error messages are already set in validateDuplicate
      return;
    }

    if (activeTab === 'departments') {
      // Find the full location object based on selected ID
      const selectedLocationObj = locations.find(loc =>
        (loc._id === formData.location) || (loc.id === formData.location)
      );

      const departmentData = {
        name: formData.name,
        code: formData.code || `DEPT_${Date.now()}`,
        description: formData.description,
        location: formData.location, // Store location reference (ID)
        isActive: formData.status === 'Active'
      };

      if (modalType === 'add') {
        createDepartmentMutation.mutate(departmentData);
      } else {
        updateDepartmentMutation.mutate({ id: selectedItem.id, departmentData });
      }
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    return status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };



  // Error state
  if (error && departments.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="text-red-600 mb-4">
          <AlertCircle className="h-12 w-12 mx-auto mb-2" />
          <h3 className="text-lg font-semibold">Error Loading Data</h3>
          <p className="text-sm">{error.message || 'Failed to load data. Please try again.'}</p>
        </div>
        <PermissionGate permission="employee_management" action="read">
          <button
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['departments'] });
            }}
            className="btn-danger"
          >
            Try Again
          </button>
        </PermissionGate>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <Building className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Department Management</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">Manage organizational structure and departments</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PermissionGate permission="employee_management" action="delete">
            {selectedRows.size > 0 && activeTab === 'departments' && (
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                className="flex items-center justify-center space-x-1.5 !bg-red-600 hover:!bg-red-700 !text-white rounded-md px-3 py-1.5 text-[13px] font-medium h-8 shadow-sm transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete Selected ({selectedRows.size})</span>
              </button>
            )}
          </PermissionGate>
          <PermissionGate permission="employee_management" action="read">
            <button
              onClick={handleExportClick}
              disabled={exportLoading}
              className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-slate-700" /> : <Download className="h-4 w-4" />}
              <span>{selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : "Export"}</span>
            </button>
          </PermissionGate>
          <PermissionGate permission="employee_management" action="create">
            <button
              onClick={handleAdd}
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
            >
              <Plus className="h-4 w-4" />
              <span>Add Department</span>
            </button>
          </PermissionGate>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6">
          {/* Search and Filter */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4 flex-1">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Search by Name, Code, Location..."
                    value={searchTerm}
                    autoFocus
                    onChange={(e) => {
                      const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                      setSearchTerm(value);
                    }}
                    onKeyPress={(e) => {
                      if (INVALID_CHARS_REGEX.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    maxLength={100}
                    className="input-field pl-10 pr-4 h-[44px]"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="input-field w-auto bg-white h-[44px]"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <DateRangeFilter
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={setDateRange}
              />

              {/* Clear Filters */}
              {(searchTerm || statusFilter !== 'all' || dateRange.startDate || dateRange.endDate) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setDateRange({ startDate: '', endDate: '' });
                  }}
                  className="btn-outline flex items-center h-[44px]"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </button>
              )}
            </div>
          </div>



          {/* Data List - Table Layout */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-semibold">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={departments.length > 0 && selectedRows.size === departments.length}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green h-4 w-4 cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-3 cursor-pointer group hover:bg-gray-100 transition-colors" onClick={() => requestSort('code')}>
                    <div className="flex items-center">
                      Code {getSortIcon('code')}
                    </div>
                  </th>
                  <th className="px-4 py-3 cursor-pointer group hover:bg-gray-100 transition-colors" onClick={() => requestSort('name')}>
                    <div className="flex items-center">
                      Department {getSortIcon('name')}
                    </div>
                  </th>
                  <th className="px-4 py-3 cursor-pointer group hover:bg-gray-100 transition-colors" onClick={() => requestSort('location')}>
                    <div className="flex items-center">
                      Location {getSortIcon('location')}
                    </div>
                  </th>
                  <th className="px-4 py-3 cursor-pointer group hover:bg-gray-100 transition-colors" onClick={() => requestSort('isActive')}>
                    <div className="flex items-center">
                      Status {getSortIcon('isActive')}
                    </div>
                  </th>
                  <th className="px-4 py-3 cursor-pointer group hover:bg-gray-100 transition-colors" onClick={() => requestSort('createdAt')}>
                    <div className="flex items-center">
                      Created At {getSortIcon('createdAt')}
                    </div>
                  </th>
                  <th className="px-4 py-3 cursor-pointer group hover:bg-gray-100 transition-colors" onClick={() => requestSort('updatedAt')}>
                    <div className="flex items-center">
                      Updated At {getSortIcon('updatedAt')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading && departments.length === 0 ? (
                  Array(5).fill(0).map((_, index) => (
                    <tr key={index} className="animate-pulse">
                      <td colSpan="7" className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                      </td>
                    </tr>
                  ))
                ) : displayedDepartments.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-12">
                      <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No departments found</h3>
                      <p className="text-gray-600">Add new department to get started</p>
                    </td>
                  </tr>
                ) : (
                  displayedDepartments.map((item) => (
                    <tr
                      key={item.id}
                      className={`hover:bg-gray-50 transition-colors ${selectedRows.has(item.id) ? 'bg-blue-50/50' : ''}`}
                    >
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(item.id)}
                          onChange={() => handleSelectRow(item.id)}
                          className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green h-4 w-4 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        {item.code}
                      </td>
                      <td className="px-4 py-4 font-medium text-gray-900 capitalize truncate max-w-xs" title={item.name}>
                        {item.name}
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        {typeof item.location === 'object' ? buildLocationLabel(item.location) : item.location}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status || (item.isActive ? 'Active' : 'Inactive'))}`}>
                          {item.status || (item.isActive ? 'Active' : 'Inactive')}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        {item.createdAt ? formatDate(item.createdAt) : 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        {item.updatedAt ? formatDate(item.updatedAt) : 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <PermissionGate permission="employee_management" action="read">
                            <button
                              onClick={() => handleView(item)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                              title="View"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="employee_management" action="update">
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-1 text-[#0F2942] hover:bg-blue-50 rounded-full transition-colors"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="employee_management" action="delete">
                            <button
                              onClick={() => handleDeleteClick(item)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {showPaginationControls && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-6 px-4 py-3 border-t border-gray-200 space-y-3 sm:space-y-0 bg-gray-50 rounded-b-lg">
              <div className="text-sm text-gray-600">
                {pagination.totalCount > 0
                  ? `Showing ${startItem}-${endItem} of ${pagination.totalCount} departments`
                  : 'No departments to display'}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Rows per page</span>
                  <select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className="input-field w-auto py-1 px-2 text-sm"
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600">
                    Page {pagination.currentPage} of {totalPages}
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={!canGoPrev || loading}
                      className={`px-3 py-1 text-sm border rounded-md ${!canGoPrev || loading
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={!canGoNext || loading}
                      className={`px-3 py-1 text-sm border rounded-md ${!canGoNext || loading
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Bulk Delete
              </h3>
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to delete <strong>{selectedRows.size}</strong> selected departments?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This action cannot be undone and will permanently remove these records.
                Departments with active employees cannot be deleted.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                className="btn-danger disabled:opacity-50"
                disabled={bulkDeleteDepartmentsMutation.isPending}
              >
                {bulkDeleteDepartmentsMutation.isPending ? 'Deleting...' : 'Delete Selected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 !mt-0 py-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {modalType === 'add' ? 'Add' : 'Edit'} Department
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onKeyPress={handleTextKeyPress}
                  onBlur={handleFieldBlur}
                  maxLength={FIELD_LIMITS.name}
                  className={getFieldClassName('name')}
                  placeholder="Enter department name"
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.name && (
                    <p className="text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.name}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    {formData.name.length}/{FIELD_LIMITS.name} characters
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  onKeyPress={handleTextKeyPress}
                  onBlur={handleFieldBlur}
                  rows="3"
                  maxLength={FIELD_LIMITS.description}
                  className={getFieldClassName('description')}
                  placeholder="Enter department description"
                />
                <div className="mt-1 flex justify-between items-center">
                  {errors.description && (
                    <p className="text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.description}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    {formData.description.length}/{FIELD_LIMITS.description} characters
                  </p>
                </div>
              </div>

              {activeTab === 'departments' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Department Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      onKeyPress={handleTextKeyPress}
                      onBlur={handleFieldBlur}
                      maxLength={FIELD_LIMITS.code}
                      className={`${getFieldClassName('code')} ${modalType !== 'add' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      placeholder="e.g., SALES001"
                      disabled={modalType !== 'add'}
                    />
                    <div className="flex justify-between items-center mt-1">
                      {errors.code && (
                        <p className="text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.code}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 ml-auto">
                        {formData.code.length}/{FIELD_LIMITS.code} characters
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      onBlur={handleFieldBlur}
                      disabled={loadingLocations}
                      className={`${getFieldClassName('location')} ${loadingLocations ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="">
                        {loadingLocations ? 'Loading locations...' : 'Select Location'}
                      </option>
                      {locations
                        .filter(loc => loc.isActive !== false)
                        .map((loc) => {
                          const locId = loc._id || loc.id;
                          const label = buildLocationLabel(loc);
                          return (
                            <option
                              key={locId}
                              value={locId}
                              title={label}
                              className="truncate"
                            >
                              {label}
                            </option>
                          );
                        })}
                    </select>
                    {errors.location && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.location}
                      </p>
                    )}
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="flex items-center justify-end space-x-4">
                <PermissionGate permission="employee_management" action="read">
                  <button
                    type="button"
                    onClick={() => {
                      setErrors({});
                      setFormData({
                        name: '',
                        description: '',
                        status: 'Active',
                        code: '',
                        location: '',
                        level: 'Individual Contributor'
                      });
                      setShowModal(false);
                    }}
                    className="btn-cancel"
                  >
                    Cancel
                  </button>
                </PermissionGate>
                <PermissionGate permission="employee_management" action={modalType === 'add' ? "create" : "update"}>
                  <button
                    type="submit"
                    disabled={createDepartmentMutation.isPending || updateDepartmentMutation.isPending}
                    className="btn-create disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {(createDepartmentMutation.isPending || updateDepartmentMutation.isPending) ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {modalType === 'add' ? 'Adding...' : 'Updating...'}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {modalType === 'add' ? 'Add' : 'Update'}
                      </>
                    )}
                  </button>
                </PermissionGate>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Department Modal */}
      {showViewModal && viewItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 !mt-0 py-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-10 max-w-3xl w-full mx-4 mb-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-gray-900 flex items-center">
                <Building className="h-6 w-6 mr-2 text-agrosaf-green" />
                Department Details
              </h3>
              <PermissionGate permission="employee_management" action="read">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewItem(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </PermissionGate>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Department Name</label>
                    <p className="text-sm text-gray-900 font-medium break-words whitespace-pre-wrap capitalize">
                      {viewItem.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Department Code</label>
                    <p className="text-sm text-gray-900 font-medium break-words whitespace-normal">{viewItem.code || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(viewItem.status || (viewItem.isActive ? 'Active' : 'Inactive'))}`}>
                      {viewItem.status || (viewItem.isActive ? 'Active' : 'Inactive')}
                    </span>
                  </div>
                  {/* Location Details */}
                  {viewItem.location ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Location Name</label>
                        <p className="text-sm text-gray-900 break-words whitespace-pre-wrap capitalize">
                          {viewItem.location.name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Country</label>
                        <p className="text-sm text-gray-900 break-words whitespace-pre-wrap capitalize">
                          {viewItem.location.country?.name || viewItem.location.address?.country?.name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">State</label>
                        <p className="text-sm text-gray-900 break-words whitespace-pre-wrap capitalize">
                          {viewItem.location.address?.state?.name || viewItem.location.state?.name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">City</label>
                        <p className="text-sm text-gray-900 break-words whitespace-pre-wrap capitalize">
                          {viewItem.location.address?.city?.name || viewItem.location.city?.name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Street / Address</label>
                        <p className="text-sm text-gray-900 break-words whitespace-pre-wrap capitalize">
                          {viewItem.location.address?.street || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Postal Code</label>
                        <p className="text-sm text-gray-900 break-words whitespace-pre-wrap">
                          {viewItem.location.address?.postalCode || 'N/A'}
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-500 mb-1">Full Address</label>
                        <p className="text-sm text-gray-900 break-words whitespace-pre-wrap capitalize">
                          {viewItem.location.address?.fullAddress || viewItem.location.fullAddress || 'N/A'}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Location</label>
                      <p className="text-sm text-gray-900 break-words whitespace-pre-wrap">N/A</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Employee Count</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Users className="h-4 w-4 mr-1 text-gray-400" />
                      {viewItem.employeeCount || 0} employees
                    </p>
                  </div>
                </div>
              </div>

              {/* Description */}
              {viewItem.description && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Description</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap break-words first-letter:uppercase">
                    {viewItem.description}
                  </p>
                </div>
              )}

              {/* Parent Department */}
              {viewItem.parentDepartment && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Parent Department</h4>
                  <p className="text-sm text-gray-900 break-words whitespace-pre-wrap capitalize">
                    {typeof viewItem.parentDepartment === 'string'
                      ? viewItem.parentDepartment
                      : viewItem.parentDepartment.name || 'N/A'}
                  </p>
                </div>
              )}

              {/* Timestamps */}
              {(viewItem.createdAt || viewItem.updatedAt || viewItem.created_at || viewItem.updated_at) && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Timestamps</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(viewItem.createdAt || viewItem.created_at) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Created At</label>
                        <p className="text-sm text-gray-900">
                          {formatDate(viewItem.createdAt || viewItem.created_at)}
                        </p>
                      </div>
                    )}
                    {(viewItem.updatedAt || viewItem.updated_at) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Updated At</label>
                        <p className="text-sm text-gray-900">
                          {formatDate(viewItem.updatedAt || viewItem.updated_at)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="flex justify-end pt-4 border-t">
                <PermissionGate permission="employee_management" action="read">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setViewItem(null);
                    }}
                    className="btn-cancel"
                  >
                    Close
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Delete
              </h3>
              <PermissionGate permission="employee_management" action="read">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedItemToDelete(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </PermissionGate>
            </div>

            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to delete the department <strong>"{selectedItemToDelete?.name}"</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <PermissionGate permission="employee_management" action="read">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedItemToDelete(null);
                  }}
                  className="btn-cancel"
                >
                  Cancel
                </button>
              </PermissionGate>
              <PermissionGate permission="employee_management" action="delete">
                <button
                  onClick={handleDelete}
                  className="btn-danger disabled:opacity-50"
                  disabled={deleteDepartmentMutation.isPending}
                >
                  {deleteDepartmentMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        onExportRange={handleExportRange}
        hasSelected={selectedRows.size > 0}
        loading={exportLoading}
        title="Export Departments"
        showDateRange={true}
      />
    </div>
  );
};

export default DepartmentRoleMapping;

